import { Alert, Box, CircularProgress, useTheme } from "@mui/material";
import * as React from "react";

import {
  type BaseTableProps,
  DatasetsTable,
  TopicsTable,
  TopicMessagePathsTable,
  EventResultsTable,
} from "@/shared/components/SearchResultViews";
import {
  TablePageState,
  TablePageAction,
  tablePageReducer,
} from "@/shared/components/SearchResultViews/stateReducer";
import { QueryTarget } from "@/shared/domain/query";
import { useQueryRecord, useQueryResults } from "@/shared/domain/query/hooks";
import { ErrorMonitoringService } from "@/shared/services";
import { Dataset, SearchQueryBody } from "@/types";

import { FileResults } from "./FileResults";
import { OnResultInteraction, SearchResultInteraction } from "./interaction";

const noop = () => {};

export interface SearchResultsProps {
  queryId?: string;
  lastError: Error | null;
  onError: (error: Error | null) => void;
  onLoaded?: () => void;
  onResultInteraction?: OnResultInteraction;
  selectedDatasets?: Set<string>;
  setSelectedDatasets?: (arg: Set<string>) => void;
  extraHeight?: number;
}

const initialQuery: SearchQueryBody = {
  sort_by: "created",
  sort_direction: "DESC",
};

export const SearchResults: React.FC<SearchResultsProps> = ({
  queryId,
  lastError,
  onError = noop,
  onLoaded = noop,
  onResultInteraction,
  selectedDatasets,
  setSelectedDatasets,
  extraHeight = 0,
}) => {
  const theme = useTheme();
  const queryRecordQuery = useQueryRecord(queryId);
  const queryRecord = queryRecordQuery.data;

  const { resultsQuery, countQuery } = useQueryResults(queryRecord?.query_id);

  const [fileTableState, fileTableDispatch] = React.useReducer<
    React.Reducer<TablePageState, TablePageAction>
  >(
    tablePageReducer,
    createTableState(["name", "created", "source", "association_id", "tags"]),
  );
  const [datasetTableState, datasetTableDispatch] = React.useReducer<
    React.Reducer<TablePageState, TablePageAction>
  >(
    tablePageReducer,
    createTableState(["dataset_id", "name", "created", "source", "tags"]),
  );
  const [topicsTableState, topicsTableDispatch] = React.useReducer<
    React.Reducer<TablePageState, TablePageAction>
  >(
    tablePageReducer,
    createTableState(["topic_id", "topic_name", "created", "message_count"]),
  );
  const [messagePathsTableState, messagePathsTableDispatch] = React.useReducer<
    React.Reducer<TablePageState, TablePageAction>
  >(
    tablePageReducer,
    createTableState([
      "message_path_id",
      "message_path",
      "topic_id",
      "data_type",
      "created",
    ]),
  );
  const [eventsTableState, eventsTableDispatch] = React.useReducer(
    tablePageReducer,
    createTableState(["event_id", "name", "start_time", "created", "tags"]),
  );

  const results = React.useMemo(() => resultsQuery.data ?? [], [resultsQuery]);
  const count = countQuery.data;

  const isLoading = queryRecordQuery.isLoading || resultsQuery.isLoading;
  const queryError = queryRecordQuery.error || resultsQuery.error;

  // 100vh - navTopBarHeight - searchBarHeight - any margins/padding - pagination
  const maxTableHeight = `calc(100vh - ${
    theme.navTopBarHeight
  } - 56px - 35px - ${parseInt(theme.spacing(3))}px) - 49px - ${extraHeight}px`;

  const fetchMoreData = React.useCallback(() => {
    if (!resultsQuery.isFetching) {
      resultsQuery
        .fetchNextPage()
        .catch((err) => ErrorMonitoringService.captureError(err));
    }
  }, [resultsQuery]);

  const makeBaseProps = React.useCallback(
    function makeBaseProps<T>(): BaseTableProps<T> {
      return {
        data: (results as T[]) || [],
        dataCount: count,
        loading: isLoading,
        fetchMoreData,
        containerStyle: {
          transition: "max-width 0.3s ease-in-out",
          maxHeight: maxTableHeight,
        },
      };
    },
    [results, count, isLoading, fetchMoreData, maxTableHeight],
  );

  React.useEffect(() => {
    onError(queryError);
  }, [onError, queryError]);

  React.useEffect(() => {
    if (!isLoading) {
      onLoaded();
    }
  }, [onLoaded, isLoading]);

  if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Datasets
  ) {
    const onDatasetSingleClick = (dataset: Dataset) => {
      if (onResultInteraction) {
        onResultInteraction(
          dataset,
          QueryTarget.Datasets,
          SearchResultInteraction.SingleClick,
        );
      }
    };

    return (
      <DatasetsTable
        {...makeBaseProps()}
        selectedDatasets={selectedDatasets}
        setSelectedDatasets={setSelectedDatasets}
        onDatasetSingleClick={onDatasetSingleClick}
        datasetTableState={datasetTableState}
        datasetTableDispatch={datasetTableDispatch}
      />
    );
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Files
  ) {
    return (
      <FileResults
        {...makeBaseProps()}
        extraHeight={extraHeight}
        fileTableState={fileTableState}
        fileTableDispatch={fileTableDispatch}
      />
    );
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Topics
  ) {
    {
      return (
        <TopicsTable
          {...makeBaseProps()}
          selectedRows={undefined}
          setSelectedRows={noop}
          onRowSingleClick={noop}
          topicsTableState={topicsTableState}
          topicsTableDispatch={topicsTableDispatch}
        />
      );
    }
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.TopicMessagePaths
  ) {
    return (
      <TopicMessagePathsTable
        {...makeBaseProps()}
        selectedRows={undefined}
        setSelectedRows={noop}
        onRowSingleClick={noop}
        messagePathsTableState={messagePathsTableState}
        messagePathsTableDispatch={messagePathsTableDispatch}
      />
    );
  } else if (
    !isLoading &&
    queryRecord &&
    queryRecord.target === QueryTarget.Events
  ) {
    return (
      <EventResultsTable
        {...makeBaseProps()}
        eventsTableState={eventsTableState}
        eventsTableDispatch={eventsTableDispatch}
      />
    );
  } else if (!isLoading && queryRecord && queryRecord.target) {
    return (
      <Box mt={theme.spacing(2)}>
        <Alert severity="error">
          Search results cannot be displayed. Please contact support@roboto.ai
          if this problem persists.
        </Alert>
      </Box>
    );
  } else if (!queryError && !lastError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: theme.spacing(3),
        }}
      >
        <CircularProgress size="2rem" />
      </Box>
    );
  }
};

const createTableState = (columns: string[]): TablePageState => ({
  modalOpen: false,
  rightClickedRow: null,
  sidebar: { isOpen: false, dataset: null },
  columns,
  rowsPerPage: 25,
  snackbar: { isOpen: false, message: "" },
  sortColumnIndex: 1,
  sortOrder: "descending",
  lastSearchQuery: initialQuery,
  selectedRows: new Set<string>(),
});
